import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { hasStoredRefreshToken, clearStoredRefreshToken } from '@/utils/refreshTransport';
import type { SignoffLayout } from '@/lib/signoff';
import type { VendorTypeOption, VendorCategoryOption } from '@/lib/vendorTaxonomy';

export type ModuleKey = 'leads' | 'sites' | 'smallworks' | 'estimates' | 'interiors' | 'employees' | 'attendance' | 'vendors' | 'ledger' | 'reports' | 'payroll' | 'employee_loans' | 'inventory' | 'officeexpenses' | 'notes' | 'contractors' | 'banking' | 'ai' | 'payments';

/** Commercial packaging above the module keys. Mirrors PRODUCT_KEYS in
 *  builder-API/config/catalog.js. */
/**
 * The actions a grant can cover beyond read/write. Mirrors MODULE_ACTIONS in the
 * API's models/User.js.
 *
 * `certify`, `authorize` and `settle` are FINANCIAL AUTHORITY over subcontracted
 * work and are deliberately NOT implied by any access level — see canDoAction.
 */
export type ModuleAction =
  | 'create' | 'edit' | 'delete' | 'approve' | 'export'
  | 'certify' | 'authorize' | 'settle';

export type ProductKey = 'crm' | 'projects' | 'smallworks' | 'interiors' | 'estimates' | 'procurement' | 'hr' | 'finance' | 'officeexpenses' | 'reports' | 'notes' | 'contractors' | 'banking' | 'ai' | 'payments';

/**
 * A product the tenant bought. Present only on tenants onboarded onto the
 * product grain; absent entirely for legacy tenants, which is how the admin UI
 * knows to keep showing them the module editor.
 *
 * NOT an access-control input. Gating reads `BuilderSnapshot.modules`, which
 * the server materializes from these — deriving access from products in the
 * browser would create a second, divergent authority.
 */
export interface TenantProduct {
  key: ProductKey;
  status: 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled' | 'expired';
  planId?: string;
  trialEndsAt?: string | null;
  expiresAt?: string | null;
  /** The product version this tenant was sold at; entitlements resolve against
   *  it, so a catalog change never widens their access. */
  catalogVersion: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'member' | 'custom' | string;
  /** Only present for role 'custom' — per-module read/write grants. */
  moduleAccess?: Partial<Record<ModuleKey, 'read' | 'write'>>;
  /**
   * Which named template produced those grants ('site_engineer', 'accountant',
   * 'custom'). A LABEL for shaping the UI to someone's job — never an access
   * input. Gating reads `moduleAccess` above, exactly as the server does.
   */
  accessProfile?: string;
  /**
   * Per-module ACTION exceptions, sparse — only what an admin deliberately
   * changed away from what the level implies. Mirrors User.modulePermissions in
   * the API. Read by lib/moduleAccess.ts canDoAction, which is the only place
   * that should interpret it.
   */
  modulePermissions?: Partial<Record<ModuleKey, Partial<Record<ModuleAction, boolean>>>>;
  /** Sites this user is limited to. EMPTY = unrestricted, not "no sites". */
  assignedSites?: string[];
  /** The same fact stated: 'all' | 'assigned' | 'selected'. */
  siteScopeMode?: 'all' | 'assigned' | 'selected';
  /** The staff record this login is linked to — source of 'assigned' scope. */
  employeeId?: string | null;
  /**
   * Money visibility. Absent on a field = "as the role implies", NOT denied —
   * see lib/useFinancialAccess.ts, which mirrors the server's rule.
   */
  financialAccess?: { projectCost?: boolean; cashPayments?: boolean };
  builderId?: string | null;
  superAdmin?: boolean;
  isActive?: boolean;
  /** Presigned URL for the user's profile photo. */
  avatarUrl?: string;
  phone?: string;
  /** Profile-page metadata (from publicUser). */
  createdAt?: string | null;
  updatedAt?: string | null;
  lastLoginDate?: string | null;
  passwordChangedAt?: string | null;
}

/** One stage in a tenant's customizable lead pipeline (see Lead Status page). */
export interface LeadStatus {
  /** Stable slug stored on leads; never changes once created. */
  key: string;
  label: string;
  /** Hex colour for the chip/column (e.g. '#1d4ed8'). */
  color?: string;
  /** Semantic role driving analytics: open = in pipeline, won/lost = closed. */
  type: 'open' | 'won' | 'lost';
  order: number;
  /** The status applied to newly-created leads. Exactly one active status has this. */
  isDefault?: boolean;
  /** Hidden from pickers but kept on historical leads. */
  archived?: boolean;
}

export type { VendorTypeOption, VendorCategoryOption } from '@/lib/vendorTaxonomy';


export interface BuilderSnapshot {
  _id: string;
  name: string;
  plan: 'free' | 'basic' | 'custom';
  modules: ModuleKey[];
  /**
   * Modules the tenant admin has hidden from the sidebar (Settings → Modules).
   * A tidiness preference, NOT an entitlement: `modules` stays what they own,
   * the API still serves every one of them, and only navigation subtracts this.
   * Read it through navModules() in lib/moduleAccess — never inline, so the
   * distinction between "doesn't have it" and "doesn't want to see it" holds.
   */
  hiddenModules?: ModuleKey[];
  /** What they bought. Display only — plan badges, trial banners, upsell. */
  products?: TenantProduct[];
  planExpiresAt?: string | null;
  gracePeriodEndsAt?: string | null;
  isActive: boolean;
  /** Presigned URL for the letterhead header image (printed documents). */
  letterheadHeaderUrl?: string;
  /** Scanned signature of the authorised signatory, printed on receipts. */
  signatureUrl?: string;
  /** The firm's rubber stamp / seal, printed beside the signature. */
  stampUrl?: string;
  signatoryName?: string;
  /** Where the two sit in a document's sign-off band. See lib/signoff.ts. */
  signoffLayout?: SignoffLayout;
  /** Presigned URL for the letterhead footer image (printed documents). */
  letterheadFooterUrl?: string;
  payrollCycleStartDay?: number;
  payrollDefaultPaymentMethod?: 'bank_transfer' | 'cash' | 'upi' | 'cheque';
  /** Presigned URL for the company logo (sidebar, print headers, invoices). */
  logoUrl?: string;
  /**
   * Upload entitlements. Presentation only — the server enforces the real gate
   * on every upload. Used to HIDE surfaces that would otherwise open onto
   * "uploads are not enabled for your account": a tab that can only apologise
   * is worse than an absent one.
   */
  uploads?: { siteFilesEnabled?: boolean };
  /** GST & tax-invoice details (used as the supplier block on printed bills). */
  gstin?: string;
  /** 2-digit GST state code, server-derived from the GSTIN. */
  stateCode?: string;
  /** Legal/registered company name (falls back to `name` when blank). */
  legalName?: string;
  /** Registered address printed on the tax invoice. */
  address?: string;
  /** Bill amount (₹) above which a non-admin's verify needs admin approval. 0 = off. */
  billApprovalThreshold?: number;
  /** Whether GST is applied on bills/POs/receipts. Default true. */
  gstEnabled?: boolean;
  /** Default GST rate (%) prefilled on new line items (₹0–28). Default 18. */
  defaultGstRate?: number;
  /** Whether TDS is deducted on vendor payments. Default false. */
  tdsEnabled?: boolean;
  /** Default TDS rate (%) prefilled on vendor payments. Default 0. */
  defaultTdsRate?: number;
  /** Default TDS section (e.g. 194C) prefilled on vendor payments. */
  defaultTdsSection?: string;
  /** Tenant's customizable lead pipeline. Empty/absent → UI falls back to defaults. */
  leadStatuses?: LeadStatus[];
  /** Tenant's lead-label palette (colored tags, separate from the pipeline stage). */
  leadLabels?: LeadLabel[];
  /** Tenant's lead categories (verticals, e.g. Construction / Jobs). */
  leadCategories?: LeadCategory[];
  /** Meta instant-form → category mapping used to auto-route multi-campaign leads. */
  leadFormMap?: LeadFormMapping[];
  /**
   * Tenant's vendor vocabulary — WHO a vendor is (`vendorTypes`) and WHICH
   * TRADE they work in (`vendorCategories`). Empty/absent → the UI falls back
   * to the built-in lists in lib/vendorTaxonomy. Read via useVendorTaxonomy().
   */
  vendorTypes?: VendorTypeOption[];
  vendorCategories?: VendorCategoryOption[];
  /**
   * Tenant's office-expense buckets. Always RESOLVED by the server — a tenant
   * who has customized nothing reads the built-in thirteen here, so the
   * editor and the pickers never have to hold a fallback list of their own.
   */
  officeCategories?: { key: string; label: string; archived?: boolean }[];
}

export interface LeadLabel {
  /** Stable slug stored on leads. */
  key: string;
  label: string;
  /** Hex colour for the chip (e.g. '#b45309'). */
  color?: string;
  order?: number;
}

/** One lead category (vertical). A lead's `category` field stores the key. */
export interface LeadCategory {
  /** Stable slug stored on leads; never changes once created. */
  key: string;
  label: string;
  /** Hex colour for the chip (e.g. '#0ea5e9'). */
  color?: string;
  /** Keep this category out of the sales pipeline list + metrics (e.g. Jobs). */
  excludeFromPipeline?: boolean;
  /** Keywords matched against a form's name + questions to auto-detect this
   *  category for new/unknown forms. Empty → built-in defaults for known names. */
  matchKeywords?: string[];
  order?: number;
  /** Applied to leads matching no form mapping. At most one active. */
  isDefault?: boolean;
  /** Hidden from pickers but kept on historical leads. */
  archived?: boolean;
}

/** Maps a Meta instant-form id to the category leads from that form get. */
export interface LeadFormMapping {
  formId: string;
  formName?: string;
  categoryKey: string;
}

export interface ImpersonationContext {
  actorId: string;
  actorName: string;
  mode: 'readonly' | 'rw';
  /** Stored so we can restore the operator session on exit. */
  originalToken: string;
  originalUser: User;
  originalBuilder: BuilderSnapshot | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  builder: BuilderSnapshot | null;
  impersonation: ImpersonationContext | null;
  isHydrated: boolean;
  isValidating: boolean;
  /**
   * Unix ms at which the access token expires, decoded from its `exp` claim.
   * Drives proactive refresh so a request rarely has to fail first.
   */
  tokenExpiresAt: number | null;
  /** True while a cold tab is trading its refresh cookie for a new session. */
  sessionRestoring: boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  builder: null,
  impersonation: null,
  isHydrated: false,
  isValidating: false,
  tokenExpiresAt: null,
  sessionRestoring: false,
};

const STORAGE_KEY = 'auth-storage';
/**
 * Legacy marker from the old "Remember me" checkbox, which chose between
 * localStorage and sessionStorage. Sessions are now always browser-wide, so
 * this is only removed, never written. Delete once no client can still hold one.
 */
const LEGACY_REMEMBER_KEY = 'auth-remember';

/**
 * Sessions live in localStorage, and only localStorage.
 *
 * The previous behaviour keyed this on the remember-me checkbox and defaulted
 * to sessionStorage, which is per-tab — so opening the app in a second tab
 * found nothing and bounced to /login. That is the bug this whole change
 * exists to fix; do not reintroduce a sessionStorage branch here.
 */
const persist = (
  user: User | null,
  token: string | null,
  builder: BuilderSnapshot | null,
  impersonation: ImpersonationContext | null = null,
) => {
  if (typeof window === 'undefined') return;
  try {
    // Transitional cleanup: drop any per-tab session left over from the old
    // behaviour so it can't shadow the localStorage one on the next hydrate.
    sessionStorage.removeItem(STORAGE_KEY);
    if (user && token) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state: { user, token, builder, impersonation } }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* storage may be unavailable (private mode quota, etc.) */ }
};

/**
 * Non-secret marker the API sets beside the HttpOnly refresh cookie. Its
 * presence means a session probably exists in this browser.
 */
const hasSessionHintCookie = () => {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((c) => c.trim().startsWith('bsn_sess='));
};

/**
 * Is there anything in this browser worth trading for a fresh access token?
 *
 * The cookie hint covers the cookie transport; a stored refresh token covers
 * the body transport, which has no cookie at all. Checking only the former is
 * what made body-transport browsers skip the restore entirely and land on
 * /login after every access-token expiry.
 */
const hasRestorableSession = () => hasSessionHintCookie() || hasStoredRefreshToken();

/** Renew this far ahead of expiry rather than waiting for a request to fail. */
const REFRESH_LEAD_MS = 90_000;

/**
 * Read the `exp` claim without pulling in a JWT library. A malformed token
 * simply yields null, and the reactive 401 path still covers us.
 */
const readTokenExpiry = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<{ user: User; token: string; builder?: BuilderSnapshot | null }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.builder = action.payload.builder ?? null;
      state.tokenExpiresAt = readTokenExpiry(state.token);
      persist(state.user, state.token, state.builder, state.impersonation);
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      persist(state.user, state.token, state.builder, state.impersonation);
    },
    /**
     * Swap just the auth token — after a password change rotates it, or after
     * /auth/refresh mints a new one. The axios request interceptor reads the
     * token from THIS store, so updating only localStorage by hand leaves the
     * old, now-revoked token in use and the next request 401s (audit B-1).
     * Goes through persist() so storage stays in sync too.
     */
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.tokenExpiresAt = readTokenExpiry(state.token);
      persist(state.user, state.token, state.builder, state.impersonation);
    },
    setBuilder: (state, action: PayloadAction<BuilderSnapshot | null>) => {
      state.builder = action.payload;
      persist(state.user, state.token, state.builder, state.impersonation);
    },
    logout: (state) => {
      // Clear localStorage FIRST so any other tab listening on the
      // 'storage' event sees us logged out before our Redux state
      // reset fires. This also defends against a render-from-storage
      // race where a hydrate call could otherwise repopulate state
      // mid-logout.
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(LEGACY_REMEMBER_KEY);
          // Body-transport refresh token. Leaving it behind would let the next
          // cold tab restore the session the user just ended.
          clearStoredRefreshToken();
        } catch {
          // storage may be unavailable (private mode quota, etc.);
          // state reset below is still authoritative for this tab.
        }
      }
      state.user = null;
      state.token = null;
      state.builder = null;
      state.impersonation = null;
      state.tokenExpiresAt = null;
      state.sessionRestoring = false;
    },
    /**
     * Begin impersonation. We stash the operator's current session in
     * `impersonation.original*` so we can restore it on exit. Token + user
     * swap to the impersonated identity.
     */
    startImpersonation: (state, action: PayloadAction<{
      token: string;
      user: User;
      builder: BuilderSnapshot | null;
      actorId: string;
      actorName: string;
      mode: 'readonly' | 'rw';
    }>) => {
      const p = action.payload;
      if (!state.user || !state.token) return;
      state.impersonation = {
        actorId: p.actorId,
        actorName: p.actorName,
        mode: p.mode,
        originalToken: state.token,
        originalUser: state.user,
        originalBuilder: state.builder,
      };
      state.user = p.user;
      state.token = p.token;
      state.builder = p.builder;
      state.tokenExpiresAt = readTokenExpiry(state.token);
      persist(state.user, state.token, state.builder, state.impersonation);
    },
    /**
     * Leave impersonation and go back to being the operator.
     *
     * Prefer the freshly-minted token the server returns from
     * /impersonate/end: with 15-minute access tokens the operator's stashed
     * `originalToken` has almost always expired during a 30-minute
     * impersonation session, so restoring it would sign them out. The stash is
     * kept only as a fallback for sessions started before this change.
     */
    endImpersonation: (state, action: PayloadAction<{ token?: string; user?: User } | undefined>) => {
      if (!state.impersonation) return;
      state.user = action?.payload?.user || state.impersonation.originalUser;
      state.token = action?.payload?.token || state.impersonation.originalToken;
      state.builder = state.impersonation.originalBuilder;
      state.impersonation = null;
      state.tokenExpiresAt = readTokenExpiry(state.token);
      persist(state.user, state.token, state.builder, null);
    },
    setHydrated: (state, action: PayloadAction<boolean>) => {
      state.isHydrated = action.payload;
    },
    setValidating: (state, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setSessionRestoring: (state, action: PayloadAction<boolean>) => {
      state.sessionRestoring = action.payload;
    },
    hydrateFromStorage: (state) => {
      if (typeof window !== 'undefined') {
        try {
          // localStorage ONLY. Reading sessionStorage first is what used to
          // make a session invisible to every other tab.
          const authStorage = localStorage.getItem(STORAGE_KEY);
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            const src = parsed.state || parsed; // tolerate the legacy flat shape
            state.user = src.user || null;
            state.token = src.token || null;
            state.builder = src.builder || null;
            state.impersonation = src.impersonation || null;
            state.tokenExpiresAt = readTokenExpiry(state.token);
          }
        } catch (error) {
          console.error('Error loading auth from localStorage:', error);
        }
      }
      state.isHydrated = true;
      /**
       * Decide here, in the SAME update that sets isHydrated, whether a cold
       * tab needs to trade its refresh cookie for a session.
       *
       * This must not be deferred to an effect: the route-guard effect runs in
       * the same commit as any effect that dispatches, so it would still see
       * `sessionRestoring === false`, find no token, and log the user out
       * while the refresh was in flight. Setting both flags together means the
       * guard's very first look already knows to wait.
       */
      const expiringSoon = state.tokenExpiresAt !== null
        && state.tokenExpiresAt - Date.now() < REFRESH_LEAD_MS;
      state.sessionRestoring = (!state.token || expiringSoon) && hasRestorableSession();
    },
  },
});

export const {
  setAuth, setUser, setToken, setBuilder, logout, setHydrated, setValidating,
  setSessionRestoring, hydrateFromStorage, startImpersonation, endImpersonation,
} = authSlice.actions;
export default authSlice.reducer;
